import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { appVersion } from '../../config/branding';
import { BASE_URL } from '../../constants';
import { navigateToLatestRelease, prefetchNewStaticAssets } from '../../utils/appReleaseNavigate';

const POLL_MS = 30 * 1000;
const CONFIRM_MS = 5 * 1000;
const SNOOZE_MS = 10 * 60 * 1000;
const MAX_SNOOZE_COUNT = 3;
const snoozeUntilKey = (version) => `app-release-snooze:${version}`;
const snoozeCountKey = (version) => `app-release-snooze-count:${version}`;

const isServerNewer = (serverVersion, clientVersion) => {
	const serverParts = String(serverVersion)
		.split('.')
		.map((n) => Number(n) || 0);
	const clientParts = String(clientVersion)
		.split('.')
		.map((n) => Number(n) || 0);
	for (let i = 0; i < 3; i += 1) {
		if (serverParts[i] > clientParts[i]) return true;
		if (serverParts[i] < clientParts[i]) return false;
	}
	return false;
};

const fetchServerVersion = async () => {
	const response = await fetch(`${BASE_URL}/api/version`, { credentials: 'include' });
	if (!response.ok) return null;
	const data = await response.json();
	return data?.version ? String(data.version) : null;
};

const readSnoozeCount = (version) => Number(sessionStorage.getItem(snoozeCountKey(version)) || 0);

/**
 * 发版提示（全站，含操作页）：
 * - 确认新版本并预取静态资源后再提示（避免只在首页看到、操作页才撞 chunk）
 * - 不倒计时、不自动刷新；「稍后」按时间延期（最多 3 次）
 * - toast z-index 低于 Bootstrap Modal，不挡订单详情弹窗
 */
const AppReleaseReload = () => {
	const [serverVersion, setServerVersion] = useState(null);
	const [assetsReady, setAssetsReady] = useState(false);
	const [isPreparing, setIsPreparing] = useState(false);
	const [snoozedUntil, setSnoozedUntil] = useState(0);
	const [snoozeCount, setSnoozeCount] = useState(0);
	const [routeErrorOpen, setRouteErrorOpen] = useState(() => document.body.hasAttribute('data-app-route-error'));

	// 路由错误居中卡片展示时，隐藏发版 toast，避免双弹层
	useEffect(() => {
		const sync = () => setRouteErrorOpen(document.body.hasAttribute('data-app-route-error'));
		sync();
		const observer = new MutationObserver(sync);
		observer.observe(document.body, { attributes: true, attributeFilter: ['data-app-route-error'] });
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		let cancelled = false;
		let confirmTimer;
		let preparingVersion = null;

		const prepareAndShow = async (version) => {
			if (preparingVersion === version) return;
			preparingVersion = version;
			try {
				await prefetchNewStaticAssets();
				if (cancelled) return;
				setServerVersion(version);
				setAssetsReady(true);
				setSnoozeCount(readSnoozeCount(version));
				const until = Number(sessionStorage.getItem(snoozeUntilKey(version)) || 0);
				if (until > Date.now()) setSnoozedUntil(until);
			} catch {
				if (cancelled) return;
				setServerVersion(version);
				setAssetsReady(true);
			} finally {
				preparingVersion = null;
			}
		};

		const confirmNewer = (version) => {
			clearTimeout(confirmTimer);
			confirmTimer = setTimeout(async () => {
				try {
					const again = await fetchServerVersion();
					if (cancelled || !again || again !== version) return;
					if (!isServerNewer(again, appVersion)) return;
					await prepareAndShow(again);
				} catch {
					/* 确认失败则等下一轮轮询 */
				}
			}, CONFIRM_MS);
		};

		const check = async () => {
			try {
				const version = await fetchServerVersion();
				if (cancelled || !version) return;
				if (isServerNewer(version, appVersion)) {
					confirmNewer(version);
				} else {
					clearTimeout(confirmTimer);
					setServerVersion(null);
					setAssetsReady(false);
				}
			} catch {
				/* 网络抖动时保持当前页 */
			}
		};

		check();
		const timer = setInterval(check, POLL_MS);
		const onVisible = () => {
			if (document.visibilityState === 'visible') check();
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => {
			cancelled = true;
			clearInterval(timer);
			clearTimeout(confirmTimer);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, []);

	const outdated = Boolean(assetsReady && serverVersion && isServerNewer(serverVersion, appVersion));
	const snoozed = Boolean(serverVersion && (snoozedUntil > Date.now() || Number(sessionStorage.getItem(snoozeUntilKey(serverVersion)) || 0) > Date.now()));
	const snoozeExhausted = snoozeCount >= MAX_SNOOZE_COUNT;

	useEffect(() => {
		if (!snoozed || !snoozedUntil) return undefined;
		const remain = Math.max(0, snoozedUntil - Date.now());
		const timer = setTimeout(() => setSnoozedUntil(0), remain + 50);
		return () => clearTimeout(timer);
	}, [snoozed, snoozedUntil]);

	if (routeErrorOpen || !outdated || snoozed) return null;

	return (
		<div className='app-release-reload app-release-reload--toast' role='status' aria-live='polite' aria-label={`新版本可用 v${serverVersion}`}>
			<div className='app-release-reload__bar'>
				<div className='app-release-reload__info'>
					<span className='app-release-reload__icon' aria-hidden='true'>
						<FontAwesomeIcon icon='gift' />
					</span>
					<span className='app-release-reload__title'>新版本可用</span>
				</div>
				<div className='app-release-reload__actions'>
					{!snoozeExhausted ? (
						<button
							type='button'
							className='btn btn-falcon-default btn-sm'
							disabled={isPreparing}
							onClick={() => {
								const nextCount = readSnoozeCount(serverVersion) + 1;
								const until = Date.now() + SNOOZE_MS;
								sessionStorage.setItem(snoozeCountKey(serverVersion), String(nextCount));
								sessionStorage.setItem(snoozeUntilKey(serverVersion), String(until));
								setSnoozeCount(nextCount);
								setSnoozedUntil(until);
							}}
						>
							稍后
						</button>
					) : null}
					<button
						type='button'
						className='btn btn-primary btn-sm'
						disabled={isPreparing}
						onClick={async () => {
							setIsPreparing(true);
							await navigateToLatestRelease();
						}}
					>
						{isPreparing ? '切换中…' : '立即刷新'}
					</button>
				</div>
			</div>
		</div>
	);
};

export default AppReleaseReload;
